import { Controller } from "@hotwired/stimulus"
import { priceBlockHtml } from "../flash_price"
import { formatTND } from "../lib/currency"

export default class extends Controller {
  static targets = ["grid", "empty", "pagination"]

  // 1 tuile feature + 8 tuiles produit par page : garde le rythme bento
  // (feature en 2x2 + grille régulière) cohérent à chaque page, même
  // avec un grand catalogue.
  itemsPerPage = 9

  allItems = []
  currentPage = 1

  connect() {
    this.fetchMiniatures()
  }

  async fetchMiniatures() {
    try {
      const response = await fetch("/api/v1/parfums?format=miniature&sort=newest")
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      const items = (await response.json()).filter((p) => p.image_url)

      if (items.length === 0) {
        this.emptyTarget.hidden = false
        return
      }

      this.allItems = items
      const totalPages = Math.max(1, Math.ceil(this.allItems.length / this.itemsPerPage))
      this.currentPage = this.restoreSavedPage(totalPages)
      this.renderPage()
      this.renderPagination()
    } catch (error) {
      console.error("Erreur lors du chargement des miniatures:", error)
      this.emptyTarget.hidden = false
    }
  }

  renderPage() {
    const start = (this.currentPage - 1) * this.itemsPerPage
    const pageItems = this.allItems.slice(start, start + this.itemsPerPage)
    const [feature, ...rest] = pageItems

    let html = `<div class="miniatures-grid">`
    if (feature) html += this.featureTile(feature)
    html += rest.map((item) => this.productTile(item)).join("")
    // Les tuiles éditoriale/teaser sont du contenu marketing fixe, pas des
    // données : affichées uniquement sur la première page pour ne pas se
    // répéter à chaque page.
    if (this.currentPage === 1) {
      html += this.editorialTile()
      html += this.teaserTile()
    }
    html += `</div>`

    this.gridTarget.innerHTML = html
  }

  renderPagination() {
    if (!this.hasPaginationTarget) return
    const totalPages = Math.ceil(this.allItems.length / this.itemsPerPage)
    if (totalPages <= 1) {
      this.paginationTarget.innerHTML = ""
      return
    }

    let html = '<ul class="pagination">'
    html += `
      <li class="page-item ${this.currentPage === 1 ? "disabled" : ""}">
        <a href="#" data-action="click->miniatures#previousPage" class="page-link">«</a>
      </li>
    `
    for (const page of this.getPageNumbers(this.currentPage, totalPages)) {
      if (page === "...") {
        html += `
          <li class="page-item disabled page-item--ellipsis">
            <span class="page-link">&hellip;</span>
          </li>
        `
      } else {
        html += `
          <li class="page-item ${this.currentPage === page ? "current" : ""}">
            <a href="#" data-action="click->miniatures#goToPage" data-page="${page}" class="page-link">${page}</a>
          </li>
        `
      }
    }
    html += `
      <li class="page-item ${this.currentPage === totalPages ? "disabled" : ""}">
        <a href="#" data-action="click->miniatures#nextPage" class="page-link">»</a>
      </li>
    `
    html += "</ul>"
    this.paginationTarget.innerHTML = html
  }

  previousPage(event) {
    event.preventDefault()
    if (this.currentPage > 1) {
      this.currentPage--
      this.savePage()
      this.renderPage()
      this.renderPagination()
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  nextPage(event) {
    event.preventDefault()
    const totalPages = Math.ceil(this.allItems.length / this.itemsPerPage)
    if (this.currentPage < totalPages) {
      this.currentPage++
      this.savePage()
      this.renderPage()
      this.renderPagination()
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  goToPage(event) {
    event.preventDefault()
    const page = parseInt(event.currentTarget.dataset.page, 10)
    if (page && page !== this.currentPage) {
      this.currentPage = page
      this.savePage()
      this.renderPage()
      this.renderPagination()
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  getPageNumbers(current, total) {
    const pages = []
    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) {
        pages.push(i)
      } else if (pages[pages.length - 1] !== "...") {
        pages.push("...")
      }
    }
    return pages
  }

  getPageStorageKey() {
    return `pagination:${window.location.pathname}${window.location.search}`
  }

  restoreSavedPage(totalPages) {
    try {
      const saved = parseInt(sessionStorage.getItem(this.getPageStorageKey()), 10)
      if (saved >= 1 && saved <= totalPages) return saved
    } catch (e) {}
    return 1
  }

  savePage() {
    try {
      sessionStorage.setItem(this.getPageStorageKey(), String(this.currentPage))
    } catch (e) {}
  }

  featureTile(item) {
    return `
      <a href="/vitrine/parfums/${item.slug || item.id}" class="miniature-tile miniature-tile--feature">
        <img src="${item.image_url}" alt="${item.name}" class="miniature-tile-photo">
        <span class="miniature-tile-feature-scrim" aria-hidden="true"></span>
        <span class="miniature-tile-feature-badge">Idée cadeau</span>
        <div class="miniature-tile-feature-content">
          <h3 class="miniature-tile-name">${item.name}</h3>
          <p class="miniature-tile-price">${this.truncate(item.description, 90)}</p>
          <span class="miniature-tile-cta">Découvrir</span>
        </div>
      </a>
    `
  }

  productTile(item) {
    return `
      <a href="/vitrine/parfums/${item.slug || item.id}" class="miniature-tile">
        <img src="${item.image_url}" alt="${item.name}" class="miniature-tile-photo">
        <span class="miniature-tile-eyebrow">${item.brand ? item.brand.name : ""}</span>
        <h3 class="miniature-tile-name">${item.name}</h3>
        ${priceBlockHtml(item, { priceClass: "miniature-tile-price" })}
      </a>
    `
  }

  editorialTile() {
    return `
      <div class="miniature-tile miniature-tile--wide miniature-tile--editorial">
        <span class="miniature-tile-eyebrow">Le cadeau parfait</span>
        <h3 class="miniature-tile-name">Offrir l'expérience complète d'un parfum, sans l'engagement d'un flacon entier.</h3>
      </div>
    `
  }

  teaserTile() {
    return `
      <div class="miniature-tile miniature-tile--wide miniature-tile--teaser">
        <span class="miniature-tile-eyebrow">Et bientôt</span>
        <h3 class="miniature-tile-name">D'autres maisons rejoindront la collection</h3>
      </div>
    `
  }

  minPrice(item) {
    if (!item.variants || item.variants.length === 0) return null
    return Math.min(...item.variants.map((v) => parseFloat(v.price)))
  }

  formatCurrency(amount) {
    return formatTND(amount)
  }

  truncate(text, maxLength) {
    if (!text || text.length <= maxLength) return text || ""
    return text.substring(0, maxLength) + "..."
  }
}
